import {
  WSMenuItem,
  copyText,
  useWSModal,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import {
  AccountExpand,
  Currency,
  InvoiceRow,
  InvoiceStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useModalRefund } from "../../../../Invoices/components/ModalRefund";
import { useModalMarkInvoiceDeposited } from "../../../../Invoices/components/ModalMarkInvoiceDeposited";
import {
  queriesAccounts,
  queriesInternalAccounts,
  queriesInvoiceRows,
  queriesInvoices
} from "@query";
import { useSession } from "@hooks/useSession";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import {
  useMutationDownloadInvoicePdf,
  useMutationDownloadInvoiceReceiptPdf,
  useMutationSendInvoice
} from "../../../../query/payments/mutations";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { isCountryUS } from "../../../../shared/selectors/selectorIsDomesticMember";
import { useSetWSStore } from "../../../../store";
import { ModalDelete } from "./ModalDelete";
import { ModalDispute } from "./ModalDispute";
import { selectorInvoiceRemovalAction } from "./selectorInvoiceRemovalAction";
import { selectorInvoicePermissions } from "../../../../shared/selectors/selectorInvoicePermissions";
import { useAcceptInvoice } from "../../../../Invoices/hooks/useAcceptInvoice";
import { selectorIsInstantPayoutAllowed } from "./selectorIsInstantPayoutAllowed";
import { selectorIsInvoiceDeposited } from "./selectorIsInvoiceDeposited";

/** Statuses an invoice can still be edited in. */
const EDITABLE_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.CREATED,
  InvoiceStatus.PENDING,
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE
];

/** Awaiting or in-flight collection, so an off-platform deposit can be recorded. */
const MARK_DEPOSITED_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PAYMENT_IN_TRANSIT
];

/**
 * Statuses for which an invoice PDF exists — i.e. everything except the two
 * pre-issue states (`Created`, `Pending`). Spelled out as an allow-list rather
 * than `status !== Created && status !== Pending`, which an absent status
 * silently satisfied (PLA-3538).
 */
const PDF_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PAYMENT_IN_TRANSIT,
  InvoiceStatus.PAID,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.PAID_OFF_PLATFORM,
  InvoiceStatus.DISPUTED,
  InvoiceStatus.RETURNED,
  InvoiceStatus.CANCELLED,
  InvoiceStatus.REFUNDED,
  InvoiceStatus.PARTIALLY_REFUNDED
];

/** Funds captured, so a receipt exists. */
const RECEIPT_PDF_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.PAID,
  InvoiceStatus.PAID_OFF_PLATFORM
];

/** Sent and unpaid, so the payer can be nudged / re-charged. */
const AWAITING_PAYMENT_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE
];

/** Statuses for which a payer-facing secure link is meaningful. */
const SECURE_LINK_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.OPENED,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PAID,
  InvoiceStatus.PAYMENT_IN_TRANSIT
];

const COPY_LINK_FAILED_MESSAGE = "Failed to copy invoice link";

export const useInvoiceActions = () => {
  const { accountId } = useSession();
  const history = useHistory();
  const location = useLocation();
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId, expand: [AccountExpand.COMPLIANCE_ENTITY] },
    { enabled: !!accountId }
  );
  const isDomestic = queryAccount.data
    ? isCountryUS(queryAccount.data.complianceEntity?.address?.country)
    : false;
  const { openSnackbar } = useWSSnackbar();
  const [sendInvoice] = useMutationSendInvoice();
  // `useWSMutation` defaults `throwOnError` to false, so a failed call *resolves*
  // with `undefined` rather than throwing — awaiting it at the call site and
  // copying `link?.url` made a failure (the endpoint currently 503s) an
  // indistinguishable no-op. Copying and reporting both outcomes from the
  // mutation callbacks keeps the click always visible (PLA-3531).
  const [createInvoiceSecureLink] =
    queriesInvoices.createInvoiceSecureLink.useMutation({
      onSuccess: link => {
        if (link.url) {
          copyText(link.url);
          openSnackbar({
            type: "success",
            message: "Invoice link copied to clipboard"
          });
        } else {
          openSnackbar({ type: "warning", message: COPY_LINK_FAILED_MESSAGE });
        }
      },
      onError: () => {
        openSnackbar({ type: "warning", message: COPY_LINK_FAILED_MESSAGE });
      }
    });
  const [sendInvoiceReminder] = queriesInvoices.remindInvoice.useMutation({
    onSuccess: () => {
      openSnackbar({ type: "success", message: "Reminder sent" });
    },
    // Announcing only the success half left a failed reminder looking like it
    // was sent (PLA-3531).
    onError: () => {
      openSnackbar({ type: "warning", message: "Failed to send reminder" });
    }
  });
  const [downloadInvoicePdf] = useMutationDownloadInvoicePdf();
  const [downloadInvoiceReceiptPdf] = useMutationDownloadInvoiceReceiptPdf();
  const [acceptInvoice] = useAcceptInvoice();
  // Re-attempt collection. The v3 `payInvoice` body is optional in full, so a
  // bodyless call reproduces the v1 re-attempt: the service re-resolves the
  // payer's reusable funding source and authorization itself.
  const [retryPayment] = queriesInvoices.payInvoice.useMutation({
    dependencies: [queriesInvoiceRows.searchInvoiceRows.queryKeyBase],
    onError: () => {
      openSnackbar({ type: "warning", message: "Failed to retry payment" });
    }
  });
  const featureFlagsQuery = useQueryFeatureFlags();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const setStore = useSetWSStore();
  const modalRefund = useModalRefund();
  const modalMarkDeposited = useModalMarkInvoiceDeposited();
  const modalDelete = useWSModal(ModalDelete, { size: "S" });
  const modalDispute = useWSModal(ModalDispute, { size: "S" });

  return useCallback(
    (invoice: InvoiceRow) => {
      const menuItems: WSMenuItem[] = [];
      const invoiceId = invoice.id;
      const status = invoice.status;
      // Explicit membership test: an absent status matches no allow-list, so it
      // can never satisfy a gate by accident.
      const isStatus = (statuses: InvoiceStatus[]) =>
        !!status && statuses.includes(status);
      const {
        isCreator: canEdit,
        canAccept,
        canDispute
      } = selectorInvoicePermissions({ invoice, accountId });
      // Which removal operation this status supports, shared with `ModalDelete`
      // so the menu label and the mutation it fires cannot disagree.
      const removalAction = selectorInvoiceRemovalAction(invoice);

      // A search row always carries an id; bail rather than emit actions that
      // would fire a mutation with a missing/empty id.
      if (!invoiceId) {
        return menuItems;
      }

      // Edit
      if (canEdit && isStatus(EDITABLE_STATUSES)) {
        menuItems.push({
          icon: "edit",
          label: "Edit",
          onClick: () => {
            setStore({ invoiceFormBackPath: location.pathname });
            history.push(`/member/invoices/${invoiceId}/edit`);
          }
        });
      }

      // Duplicate — read-only w.r.t. this invoice (it opens a new draft), so it
      // is offered regardless of status.
      menuItems.push({
        icon: "plus-circle",
        label: "Duplicate",
        onClick: () => {
          history.push(`/member/invoices/${invoiceId}/duplicate`);
        }
      });

      // Delete (drafts only)
      if (canEdit && removalAction === "delete") {
        menuItems.push({
          icon: "exit",
          label: "Remove",
          onClick: () => {
            modalDelete.open({ invoice, action: removalAction });
          }
        });
      }

      // Cancel (already visible to the payer — voided, not deleted)
      if (canEdit && removalAction === "cancel") {
        menuItems.push({
          icon: "exit",
          label: "Cancel invoice",
          onClick: () => {
            modalDelete.open({ invoice, action: removalAction });
          }
        });
      }

      // Send
      if (canEdit && status === InvoiceStatus.CREATED) {
        menuItems.push({
          icon: "send",
          label: "Send now",
          onClick: () => {
            sendInvoice({ invoiceId });
          }
        });
      }

      // Mark deposited — recording an off-platform deposit is the invoice
      // owner's call, so it takes the same edit-access gate the details-screen
      // menu already applies (`InvoiceActions`), not status alone.
      if (canEdit && isStatus(MARK_DEPOSITED_STATUSES)) {
        menuItems.push({
          icon: "dollar-circle",
          label: "Mark deposited",
          onClick: () => {
            modalMarkDeposited.open({
              invoiceId,
              amount: invoice.amount ?? 0,
              currency: invoice.currency ?? Currency.USD,
              invoiceNumber: invoice.invoiceNumber ?? undefined
            });
          }
        });
      }

      // Download PDF
      if (isStatus(PDF_STATUSES)) {
        menuItems.push({
          icon: "download",
          label: "Download PDF",
          onClick: () => {
            downloadInvoicePdf({ invoiceId });
          }
        });
      }

      // Download Receipt PDF
      if (isStatus(RECEIPT_PDF_STATUSES)) {
        menuItems.push({
          icon: "download",
          label: "Download receipt PDF",
          onClick: () => {
            downloadInvoiceReceiptPdf({ invoiceId });
          }
        });
      }

      // Send reminder
      if (canEdit && isStatus(AWAITING_PAYMENT_STATUSES)) {
        menuItems.push({
          icon: "calendar",
          label: "Send reminder",
          onClick: () => {
            sendInvoiceReminder({ invoiceId });
          }
        });
      }

      // Copy link — the copy itself, and the success / failure snackbar, live in
      // the mutation's callbacks above.
      if (canEdit && isStatus(SECURE_LINK_STATUSES)) {
        menuItems.push({
          icon: "download",
          label: "Copy invoice link",
          onClick: () => {
            createInvoiceSecureLink({ invoiceId });
          }
        });
      }

      // Accept
      if (canAccept) {
        menuItems.push({
          icon: "check",
          label: "Accept invoice",
          onClick: () => {
            acceptInvoice({ invoiceId });
          }
        });
      }

      // Dispute
      if (canDispute) {
        menuItems.push({
          icon: "alert-circle",
          label: "Dispute invoice",
          onClick: () => {
            modalDispute.open({ invoice });
          }
        });
      }

      // Refund — `selectorIsInvoiceDeposited` requires a known captured status.
      if (
        canEdit &&
        featureFlagsQuery.data?.refundButton &&
        selectorIsInvoiceDeposited(invoice) &&
        queryInternalAccounts.data?.data?.some(
          account => account.type === "Clearing"
        )
      ) {
        menuItems.push({
          icon: "dollar-circle",
          label: "Refund",
          onClick: () => {
            modalRefund.open({ invoice });
          }
        });
      }

      // Retry payment
      if (
        canEdit &&
        isStatus(AWAITING_PAYMENT_STATUSES) &&
        !!invoice.recurringInvoiceId
      ) {
        menuItems.push({
          icon: "refresh-h",
          label: "Retry payment",
          onClick: () => {
            // The key is shared while the call is in flight (so a double-click
            // dedups server-side) and released once it settles: a persisted 4xx
            // replays for the same key, so a genuine re-attempt needs a new one.
            const idempotencyKey = new IdempotencyKey({
              scope: "retryInvoicePayment",
              invoiceId
            });

            retryPayment(
              {
                invoiceId,
                idempotencyKey: idempotencyKey.value,
                // Retrying from a row menu asserts no precondition — the intent
                // is "collect whatever this invoice is now".
                ifMatch: "*",
                paymentRequest: {}
              },
              {
                onSuccess: () => idempotencyKey.destroy(),
                onError: () => idempotencyKey.destroy()
              }
            );
          }
        });
      }

      // Instant payout — `selectorIsInstantPayoutAllowed` requires
      // `status === Paid`.
      if (
        selectorIsInstantPayoutAllowed(
          invoice,
          queryPayoutSettings.data,
          isDomestic
        )
      ) {
        menuItems.push({
          icon: "instant-circle",
          label: "Request instant payout",
          onClick: () => {
            history.push(`${location.pathname}/${invoiceId}/instant-deposit`);
          }
        });
      }

      return menuItems;
    },
    [
      accountId,
      acceptInvoice,
      downloadInvoicePdf,
      downloadInvoiceReceiptPdf,
      featureFlagsQuery.data?.refundButton,
      queryInternalAccounts.data,
      history,
      location.pathname,
      createInvoiceSecureLink,
      modalMarkDeposited,
      modalDelete,
      modalDispute,
      modalRefund,
      isDomestic,
      queryPayoutSettings.data,
      retryPayment,
      sendInvoice,
      sendInvoiceReminder,
      setStore
    ]
  );
};
